import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Users,
  ShoppingCart,
  Calendar,
  RefreshCw,
  ArrowRight,
  DollarSign,
  Loader2,
  BarChart3,
  Target,
  Sparkles,
  ArrowRightLeft,
  BookOpen
} from 'lucide-react';
import AnalyticsStorytelling from '@/components/AnalyticsStorytelling';

// Funnel visualization helper - defined outside component to avoid re-creation
const FunnelBar = ({ label, value, maxValue, color = 'bg-primary' }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-medium">{value.toLocaleString()}</span>
      </div>
      <div className="h-8 bg-muted rounded-lg overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Conversion rate badge - defined outside component to avoid re-creation
const ConversionBadge = ({ rate, label }) => (
  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
    <div className="text-2xl font-bold text-primary">{rate}%</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  
  // Analytics data
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [paths, setPaths] = useState(null);
  const [swaps, setSwaps] = useState(null);
  const [baskets, setBaskets] = useState(null);
  const [trends, setTrends] = useState(null);

  // Load all analytics data
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, funnelRes, pathsRes, swapsRes, basketsRes, trendsRes] = await Promise.all([
        api.get('/analytics/summary'),
        api.get(`/analytics/funnel?period=${period}`),
        api.get(`/analytics/paths?period=${period}`),
        api.get(`/analytics/swaps?period=${period}`),
        api.get(`/analytics/baskets?period=${period}`),
        api.get('/analytics/trends?days=7')
      ]);
      
      setSummary(summaryRes.data);
      setFunnel(funnelRes.data);
      setPaths(pathsRes.data);
      setSwaps(swapsRes.data);
      setBaskets(basketsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, period]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please log in to view analytics</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 bg-background" data-testid="admin-analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-semibold font-['Playfair_Display']">
                Analytics Dashboard
              </h1>
              <p className="text-muted-foreground">
                Business metrics & conversion data
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Today</SelectItem>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={loadAnalytics}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">{summary?.totals?.users || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Meal Plans</p>
                  <p className="text-2xl font-bold">{summary?.totals?.meal_plans || 0}</p>
                </div>
                <Calendar className="h-8 w-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Grocery Lists</p>
                  <p className="text-2xl font-bold">{summary?.totals?.grocery_lists || 0}</p>
                </div>
                <ShoppingCart className="h-8 w-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Basket</p>
                  <p className="text-2xl font-bold">${baskets?.avg_basket_value || 0}</p>
                </div>
                <DollarSign className="h-8 w-8 text-primary/40" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="storytelling" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="storytelling">
              <BookOpen className="h-4 w-4 mr-2" />
              Story
            </TabsTrigger>
            <TabsTrigger value="funnel">
              <Target className="h-4 w-4 mr-2" />
              Funnel
            </TabsTrigger>
            <TabsTrigger value="paths">
              <Sparkles className="h-4 w-4 mr-2" />
              Paths
            </TabsTrigger>
            <TabsTrigger value="swaps">
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Swaps
            </TabsTrigger>
            <TabsTrigger value="overview">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
          </TabsList>

          {/* Storytelling Tab - Clear Narratives */}
          <TabsContent value="storytelling">
            <AnalyticsStorytelling />
          </TabsContent>

          {/* Funnel Tab */}
          <TabsContent value="funnel">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Funnel Visualization */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Conversion Funnel
                  </CardTitle>
                  <CardDescription>
                    User journey from plan to checkout
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {funnel && (
                    <>
                      <FunnelBar 
                        label="Plan Started" 
                        value={funnel.funnel.plan_started} 
                        maxValue={funnel.funnel.plan_started}
                        color="bg-blue-500"
                      />
                      <FunnelBar 
                        label="Plan Completed" 
                        value={funnel.funnel.plan_completed} 
                        maxValue={funnel.funnel.plan_started}
                        color="bg-green-500"
                      />
                      <FunnelBar 
                        label="Cart Built" 
                        value={funnel.funnel.cart_built} 
                        maxValue={funnel.funnel.plan_started}
                        color="bg-yellow-500"
                      />
                      <FunnelBar 
                        label="Checkout Clicked" 
                        value={funnel.funnel.checkout_clicked} 
                        maxValue={funnel.funnel.plan_started}
                        color="bg-primary"
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Conversion Rates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Conversion Rates
                  </CardTitle>
                  <CardDescription>
                    Step-by-step conversion percentages
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {funnel && (
                    <div className="grid grid-cols-2 gap-4">
                      <ConversionBadge 
                        rate={funnel.conversions.plan_completion_rate} 
                        label="Plan Completion"
                      />
                      <ConversionBadge 
                        rate={funnel.conversions.cart_build_rate} 
                        label="Cart Build"
                      />
                      <ConversionBadge 
                        rate={funnel.conversions.checkout_rate} 
                        label="Checkout"
                      />
                      <div className="col-span-2 p-4 bg-primary/10 rounded-lg text-center">
                        <div className="text-3xl font-bold text-primary">
                          {funnel.conversions.overall_conversion}%
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Overall Conversion (Plan → Checkout)
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Paths Tab */}
          <TabsContent value="paths">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Most Used Paths
                </CardTitle>
                <CardDescription>
                  Which meal plan paths are most popular
                </CardDescription>
              </CardHeader>
              <CardContent>
                {paths?.paths?.length > 0 ? (
                  <div className="space-y-3">
                    {paths.paths.map((path, index) => (
                      <div 
                        key={path.path_id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                            {index + 1}
                          </Badge>
                          <div>
                            <p className="font-medium">{path.path_name}</p>
                            <p className="text-xs text-muted-foreground">{path.path_id}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{path.count}</p>
                          <p className="text-xs text-muted-foreground">plans</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No path data yet</p>
                    <p className="text-sm">Path usage will appear here as users create meal plans</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Swaps Tab */}
          <TabsContent value="swaps">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Meal Swaps */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Swapped Meals</CardTitle>
                  <CardDescription>
                    Which meals users replace most often
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {swaps?.meal_swaps?.length > 0 ? (
                    <div className="space-y-2">
                      {swaps.meal_swaps.map((swap, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm"
                        >
                          <span className="truncate flex-1">{swap.from}</span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="truncate flex-1">{swap.to}</span>
                          <Badge variant="secondary">{swap.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>No meal swap data yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ingredient Swaps */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Swapped Ingredients</CardTitle>
                  <CardDescription>
                    Common ingredient substitutions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {swaps?.ingredient_swaps?.length > 0 ? (
                    <div className="space-y-2">
                      {swaps.ingredient_swaps.map((swap, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm"
                        >
                          <span className="truncate flex-1">{swap.from}</span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="truncate flex-1">{swap.to}</span>
                          <Badge variant="secondary">{swap.count}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <p>No ingredient swap data yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Subscription Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Breakdown</CardTitle>
                  <CardDescription>Users by plan type</CardDescription>
                </CardHeader>
                <CardContent>
                  {summary?.subscriptions && (
                    <div className="space-y-3">
                      {Object.entries(summary.subscriptions).map(([plan, count]) => (
                        <div key={plan} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge variant={plan === 'free' ? 'secondary' : 'default'}>
                              {plan.charAt(0).toUpperCase() + plan.slice(1)}
                            </Badge>
                          </div>
                          <span className="font-medium">{count} users</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Basket Analytics */}
              <Card>
                <CardHeader>
                  <CardTitle>Basket Analytics</CardTitle>
                  <CardDescription>Grocery cart statistics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <p className="text-2xl font-bold">${baskets?.avg_basket_value || 0}</p>
                      <p className="text-xs text-muted-foreground">Avg Basket Value</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <p className="text-2xl font-bold">{baskets?.avg_item_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Avg Items/Cart</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <p className="text-2xl font-bold">{baskets?.total_carts || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Carts Built</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg text-center">
                      <p className="text-2xl font-bold">${baskets?.total_estimated_value || 0}</p>
                      <p className="text-xs text-muted-foreground">Total Est. Value</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Today vs This Week */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Activity Comparison</CardTitle>
                  <CardDescription>Today vs This Week</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">Plans Started</p>
                      <p className="text-lg font-bold">
                        {summary?.today?.funnel?.plan_started || 0} / {summary?.this_week?.funnel?.plan_started || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Today / Week</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">Plans Completed</p>
                      <p className="text-lg font-bold">
                        {summary?.today?.funnel?.plan_completed || 0} / {summary?.this_week?.funnel?.plan_completed || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Today / Week</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">Carts Built</p>
                      <p className="text-lg font-bold">
                        {summary?.today?.funnel?.cart_built || 0} / {summary?.this_week?.funnel?.cart_built || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Today / Week</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-1">Checkouts</p>
                      <p className="text-lg font-bold">
                        {summary?.today?.funnel?.checkout_clicked || 0} / {summary?.this_week?.funnel?.checkout_clicked || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Today / Week</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Enterprise Pitch Summary */}
        <Card className="mt-8 border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Enterprise Pitch Summary
            </CardTitle>
            <CardDescription>
              Key metrics for B2B conversations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-primary">
                  {funnel?.conversions?.overall_conversion || 0}%
                </p>
                <p className="text-sm text-muted-foreground">Overall Conversion Rate</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">
                  ${baskets?.avg_basket_value || 0}
                </p>
                <p className="text-sm text-muted-foreground">Average Basket Value</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">
                  {paths?.total_path_plans || 0}
                </p>
                <p className="text-sm text-muted-foreground">Plans via Paths Feature</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
